<template>
    <div>
        <promotion-details :promotionId="props.promotionId">
            <form-wrapper
                :sections="[
                    { id: 'name', label: 'Name' },
                    { id: 'variable', label: 'Variable' },
                ]"
                :hideNav="!!props.promotionId"
            >
                <vx-expandable-card id="name">
                    <template v-slot:header> Name</template>

                    <template v-slot:content>
                        <vx-input
                            v-model="promotion.name"
                            autofocus
                            size="lg"
                            :error="displayFirstError(validation.name)"
                            :maxlength="80"
                        ></vx-input>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="variable">
                    <template v-slot:header> Message Variable</template>
                    <template v-slot:headerSubtitle>
                        This is the name of the variable you will use in message builder.
                    </template>

                    <template v-slot:content>
                        <vx-input
                            v-model="promotion.code"
                            size="lg"
                            :error="displayFirstError(validation.code)"
                            :maxlength="40"
                        ></vx-input>

                        <vx-alert v-if="!!props.promotionId" class="mt-6" color="info" :closeable="false">
                            If you change this you will need to update Campaigns and Automations that rely on this
                            Message Variable manually.
                        </vx-alert>
                    </template>
                </vx-expandable-card>

                <vx-sticky-footer>
                    <vx-button href="/settings/integrations/promotions" class="w-20" size="lg" color="muted">
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="promotion-save"
                        class="grow"
                        size="lg"
                        color="primary"
                        :loading="processing"
                    >
                        {{ props.promotionId ? 'Update' : 'Create' }} Promotion
                    </vx-button>
                </vx-sticky-footer>
            </form-wrapper>
        </promotion-details>
    </div>
</template>

<script setup>
import { faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import PromotionDetails from '~/components/promotions/PromotionDetails.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { VxAlert, VxButton, VxExpandableCard, VxInput, VxStickyFooter } from '@voxie/frontend-components';
import { defaultPromotion, usePromotionDetails } from '~/components/promotions/usePromotionDetails';
import { displayFirstError, startAlphanumericDashUnderscore } from '~/utils/validation';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { maxLength, required } from '@vuelidate/validators';
import { ref } from 'vue';

const props = defineProps({
    promotionId: {
        type: Number,
        required: false,
    },
});

const { promotion, save, onMountedPromotion } = usePromotionDetails();

const savedPromotionId = ref();

const { validation, submit, processing } = useFormSubmission({
    data: promotion,
    validation: {
        name: { required, maxLength: maxLength(80) },
        code: { required, maxLength: maxLength(40), format: startAlphanumericDashUnderscore },
    },
    submit: async () => {
        const response = await save();
        savedPromotionId.value = response.id;

        return response;
    },
    reset: () => {
        if (!props.promotionId) {
            promotion.value = defaultPromotion();
        }
    },
    preserveScroll: () => Boolean(props.promotionId),
    notification: () => ({
        message: `Promotion ${props.promotionId ? 'updated' : 'created'}`,
        redirect: props.promotionId ? undefined : `/settings/integrations/promotions/${savedPromotionId.value}/coupons`,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to Promotions',
            href: '/settings/integrations/promotions',
        },
    }),
});

onMountedPromotion(() => {
    if (props.promotionId) {
        // reset validation to disable submit button after loading promotion
        validation.value.$reset();
    }
});
</script>
